import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { createHttpError } from "@/lib/api";
import { humanizeAuthError } from "@/lib/authErrors";

/** Doit respecter `profiles_role_check` en base (souvent buyer | seller | admin, pas « user »). */
const DEFAULT_PROFILE_ROLE = "buyer";

const AuthContext = createContext(null);

function mergeUser(authUser, profileRow) {
  if (!authUser && !profileRow) return null;
  const p = profileRow || {};
  const meta = authUser?.user_metadata || {};
  const firstName = p.first_name ?? meta.first_name ?? meta.prenom ?? meta.prénom ?? null;
  const lastName = p.last_name ?? meta.last_name ?? null;
  return {
    ...p,
    user_id: p.id ?? authUser?.id,
    email: authUser?.email ?? p.email,
    name: p.name ?? p.nom ?? meta.name ?? (authUser?.email ? authUser.email.split("@")[0] : ""),
    first_name: firstName,
    last_name: lastName,
    kyc_status: p.kyc_status ?? p.statut_kyc ?? meta.kyc_status ?? "none",
    role: p.role ?? DEFAULT_PROFILE_ROLE,
    charter_accepted: p.charter_accepted ?? meta.charter_accepted,
    phone: p.phone ?? p.telephone ?? meta.telephone ?? meta.phone,
    country: p.country ?? meta.country,
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  /** Synchronisé dès qu’on a le profil DB (évite merge sans profil au refresh token / INITIAL_SESSION / SIGNED_IN). */
  const knownSessionUserIdRef = useRef(null);

  const putMerged = useCallback((prof, merged) => {
    if (merged?.user_id) knownSessionUserIdRef.current = merged.user_id;
    else if (merged?.id) knownSessionUserIdRef.current = merged.id;
    else knownSessionUserIdRef.current = null;
    setProfile(prof);
    setUser(merged);
  }, []);

  /** Profil + merge sans repasser par getUser() (évite des blocages / courses après signIn / signUp). */
  const loadSessionForAuthUser = useCallback(async (authUser) => {
    if (!authUser) return { profile: null, merged: null };
    const { data: prof, error: perr } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", authUser.id)
      .maybeSingle();
    if (perr) console.warn("profiles:", perr.message);
    if (prof?.deleted_at || prof?.suspended_at || prof?.banned) {
      await supabase.auth.signOut();
      return {
        profile: null,
        merged: null,
        blocked: prof?.deleted_at ? "deleted" : "suspended",
      };
    }
    const authEmail = String(authUser.email || "").trim();
    if (authEmail && prof?.id) {
      const pe = String(prof.email || "").trim();
      if (!pe || pe.toLowerCase() !== authEmail.toLowerCase()) {
        void supabase.from("profiles").update({ email: authEmail }).eq("id", authUser.id).then(({ error: ue }) => {
          if (ue) console.warn("profiles email sync:", ue.message);
        });
      }
    }
    return { profile: prof, merged: mergeUser(authUser, prof) };
  }, []);

  const fetchProfile = useCallback(async () => {
    const { data: authData, error: authErr } = await supabase.auth.getUser();
    if (authErr || !authData.user) {
      return { profile: null, merged: null };
    }
    return loadSessionForAuthUser(authData.user);
  }, [loadSessionForAuthUser]);

  const applySession = useCallback(async () => {
    const { profile: prof, merged } = await fetchProfile();
    putMerged(prof, merged);
  }, [fetchProfile, putMerged]);

  const checkAuth = useCallback(async () => {
    try {
      await applySession();
    } catch {
      knownSessionUserIdRef.current = null;
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [applySession]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    /* Pas de parallèle `applySession()` ici : une seule suite — load profiles puis putMerged, puis setLoading false. */
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      if (session?.user) {
        void loadSessionForAuthUser(session.user)
          .then(({ profile: prof, merged }) => {
            if (cancelled) return;
            putMerged(prof, merged);
            if (!cancelled) setLoading(false);
          })
          .catch((e) => {
            console.warn("onAuthStateChange profile:", e);
            if (!cancelled) setLoading(false);
          });
      } else {
        knownSessionUserIdRef.current = null;
        setProfile(null);
        setUser(null);
        if (!cancelled) setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [loadSessionForAuthUser, putMerged]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw createHttpError(400, humanizeAuthError(error.message));
    const authUser = data.user;
    if (!authUser) throw createHttpError(400, "Session introuvable après connexion. Réessayez.");
    const mergedQuick = mergeUser(authUser, null);
    if (authUser.id) knownSessionUserIdRef.current = authUser.id;
    setProfile(null);
    setUser(mergedQuick);
    void loadSessionForAuthUser(authUser)
      .then(({ profile: prof, merged }) => {
        putMerged(prof, merged);
      })
      .catch((e) => console.warn("[auth] login profile:", e));
    return mergedQuick;
  };

  const serverSignupUrl = () => {
    const base = (
      (typeof process !== "undefined" && process.env.REACT_APP_NEXT_API_URL) ||
      (typeof process !== "undefined" && process.env.REACT_APP_BACKEND_URL) ||
      ""
    ).replace(/\/$/, "");
    if (base) return `${base}/api/auth/signup`;
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}/api/auth/signup`;
    }
    return "";
  };

  /** Inscription via API serveur (admin) : pas d’e-mail de confirmation → évite le rate limit client sur signUp. */
  const registerViaServer = async (payload) => {
    const url = serverSignupUrl();
    if (!url) return { used: false };
    const charterAcceptedAt = new Date().toISOString();
    let res;
    try {
      res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          email: payload.email,
          password: payload.password,
          name: payload.nom,
          nom: payload.nom,
          first_name: payload.first_name,
          last_name: payload.last_name,
          telephone: payload.telephone,
          company_name: payload.company_name,
          country: payload.country,
          role: "utilisateur",
          statut_kyc: "none",
          kyc_status: "none",
          charter_accepted: true,
          charter_accepted_at: charterAcceptedAt,
          professional_certified: Boolean(payload.professional_certified),
        }),
        credentials: "omit",
      });
    } catch {
      return { used: false };
    }
    const json = await res.json().catch(() => ({}));
    if (res.status === 403 && json.code === "server_signup_disabled") return { used: false };
    if (res.status === 503) return { used: false };
    if (res.status === 404) return { used: false };
    if (res.status === 403) {
      throw createHttpError(403, humanizeAuthError(json.detail || "Inscription refusée"));
    }
    if (!res.ok) {
      if (res.status === 409 || res.status === 400) {
        throw createHttpError(res.status, humanizeAuthError(json.detail || json.message || "Erreur à la création"));
      }
      return { used: false };
    }
    const uid = json.user_id;
    const { data: sdata, error: signErr } = await supabase.auth.signInWithPassword({
      email: payload.email,
      password: payload.password,
    });
    if (signErr) throw createHttpError(400, humanizeAuthError(signErr.message));
    if (!sdata.user) throw createHttpError(400, "Session introuvable. Réessayez.");
    let prof = null;
    let merged = mergeUser(sdata.user, null);
    try {
      const loaded = await Promise.race([
        loadSessionForAuthUser(sdata.user),
        new Promise((resolve) => {
          window.setTimeout(() => resolve(null), 5000);
        }),
      ]);
      if (loaded) {
        prof = loaded.profile;
        merged = loaded.merged;
      }
    } catch (e) {
      console.warn("[auth] register server profile:", e);
    }
    if (!prof && uid) {
      merged = mergeUser(
        {
          id: uid,
          email: payload.email,
          user_metadata: {
            name: payload.nom,
            telephone: payload.telephone,
            country: payload.country,
            charter_accepted: true,
            professional_certified: payload.professional_certified,
          },
        },
        {
          id: uid,
          email: payload.email,
          name: payload.nom,
          nom: payload.nom,
          telephone: payload.telephone,
          phone: payload.telephone,
          company_name: payload.company_name,
          country: payload.country,
          charter_accepted: true,
          charter_accepted_at: charterAcceptedAt,
          kyc_status: "none",
          statut_kyc: "none",
          role: "utilisateur",
        }
      );
    }
    putMerged(prof, merged);
    if (!prof) {
      void loadSessionForAuthUser(sdata.user).then(({ profile: p, merged: m }) => {
        putMerged(p, m);
      });
    }
    return { used: true, merged };
  };

  const profileUpsertFromRegister = (uid, payload) => {
    const charterAcceptedAt = new Date().toISOString();
    const company = payload.company_name && String(payload.company_name).trim() ? String(payload.company_name).trim() : null;
    const country =
      payload.country != null && String(payload.country).trim() !== ""
        ? String(payload.country).trim().toUpperCase()
        : null;
    return {
      id: uid,
      email: payload.email,
      name: payload.nom,
      nom: payload.nom,
      first_name: payload.first_name,
      last_name: payload.last_name,
      telephone: payload.telephone,
      phone: payload.telephone,
      company_name: company,
      country,
      charter_accepted: true,
      charter_accepted_at: charterAcceptedAt,
      kyc_status: "none",
      statut_kyc: "none",
      role: "utilisateur",
    };
  };

  const register = async (payload) => {
    const server = await registerViaServer(payload);
    if (server.used) return server.merged;

    const { data, error } = await supabase.auth.signUp({
      email: payload.email,
      password: payload.password,
      options: {
        data: {
          name: payload.nom,
          nom: payload.nom,
          first_name: payload.first_name,
          last_name: payload.last_name,
          telephone: payload.telephone,
          country: payload.country,
          company_name: payload.company_name || "",
          charter_accepted: true,
          professional_certified: payload.professional_certified,
        },
      },
    });
    if (error) throw createHttpError(400, humanizeAuthError(error.message));
    // Certaines configs Supabase (ex: email confirmation) renvoient un user sans session.
    // Pour "accès immédiat", on tente une connexion juste après.
    if (!data?.session && payload?.email && payload?.password) {
      const { data: sdata, error: signErr } = await supabase.auth.signInWithPassword({
        email: payload.email,
        password: payload.password,
      });
      if (signErr) throw createHttpError(400, humanizeAuthError(signErr.message));
      if (sdata?.session) {
        data.session = sdata.session;
      }
      if (sdata?.user && !data.user) {
        data.user = sdata.user;
      }
    }
    const uid = data.user?.id;
    if (uid) {
      const row = profileUpsertFromRegister(uid, payload);
      const { error: pe } = await supabase.from("profiles").upsert(row, { onConflict: "id" });
      if (pe) throw createHttpError(400, humanizeAuthError(pe.message));
    }
    let { profile: prof, merged } = data.user
      ? await loadSessionForAuthUser(data.user)
      : await fetchProfile();
    if (!merged && uid) {
      merged = mergeUser(
        {
          id: uid,
          email: payload.email,
          user_metadata: {
            name: payload.nom,
            telephone: payload.telephone,
            country: payload.country,
            charter_accepted: true,
            professional_certified: payload.professional_certified,
          },
        },
        profileUpsertFromRegister(uid, payload)
      );
    }
    putMerged(prof, merged);
    return merged;
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch {}
    localStorage.removeItem("lm_token");
    knownSessionUserIdRef.current = null;
    setUser(null);
    setProfile(null);
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider
      value={{ user, profile, loading, login, register, logout, refresh, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
